import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Badge,
  Text,
  Box,
  useColorModeValue,
  Link,
} from '@chakra-ui/react'
import { ExternalLinkIcon } from '@chakra-ui/icons'
import { colors } from '../../theme'
import { STRINGS } from '../../resources'
import { unescapeText } from '../../utils/text'
import type { Vulnerability } from '../../types/vulnerability'
import styles from './ComparisonModal.module.css'

interface ComparisonModalProps {
  vulnerabilities: Vulnerability[]
  isOpen: boolean
  onClose: () => void
}

// Use shared colorScheme from design tokens
const SEVERITY_BADGE_COLORS = colors.severityColorScheme


export function ComparisonModal({
  vulnerabilities,
  isOpen,
  onClose,
}: ComparisonModalProps) {
  const cardBg = useColorModeValue(colors.ui.card.light, colors.ui.card.dark)
  const borderColor = useColorModeValue(colors.ui.border.light, colors.ui.border.dark)
  const labelColor = useColorModeValue(colors.ui.text.secondary.light, colors.ui.text.secondary.dark)
  const diffBg = useColorModeValue('yellow.100', 'yellow.800')
  const uniqueBg = useColorModeValue('blue.50', 'blue.900')
  const uniqueBorder = useColorModeValue('blue.300', 'blue.600')
  const riskFactorBg = useColorModeValue('gray.100', 'gray.700')

  if (vulnerabilities.length === 0) return null

  // Find differences for highlighting
  const allCvssSame = vulnerabilities.every((v) => v.cvss === vulnerabilities[0].cvss)
  const allPackagesSame = vulnerabilities.every((v) => v.packageName === vulnerabilities[0].packageName)

  // Collect all risk factors and find unique ones per vulnerability
  const allRiskFactors = new Set<string>()
  vulnerabilities.forEach((v) => v.riskFactors.forEach((rf) => allRiskFactors.add(rf)))

  const getUniqueRiskFactors = (vuln: Vulnerability) => {
    const othersRiskFactors = new Set<string>()
    vulnerabilities
      .filter((v) => v.id !== vuln.id)
      .forEach((v) => v.riskFactors.forEach((rf) => othersRiskFactors.add(rf)))
    return vuln.riskFactors.filter((rf) => !othersRiskFactors.has(rf))
  }

  // Find highest CVSS
  const highestCvss = Math.max(...vulnerabilities.map((v) => v.cvss))

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {STRINGS.COMPARISON.TITLE} ({vulnerabilities.length})
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Box className={styles.selectionHint} bg={uniqueBg}>
            <Text color={labelColor}>
              {STRINGS.COMPARISON.HINT}
            </Text>
          </Box>

          <div className={styles.grid}>
            {vulnerabilities.map((vuln) => {
              const uniqueRiskFactors = getUniqueRiskFactors(vuln)
              const isHighestCvss = vuln.cvss === highestCvss && !allCvssSame

              return (
                <Box
                  key={vuln.id}
                  className={styles.card}
                  bg={cardBg}
                  borderColor={borderColor}
                >
                  {/* Header */}
                  <Box className={styles.cardHeader} borderColor={borderColor}>
                    <Badge
                      colorScheme={SEVERITY_BADGE_COLORS[vuln.severity]}
                      variant="solid"
                    >
                      {vuln.severity.toUpperCase()}
                    </Badge>
                    <Text className={styles.cveTitle}>{vuln.cve}</Text>
                  </Box>

                  {/* CVSS */}
                  <Box className={styles.section}>
                    <Text className={styles.sectionLabel} color={labelColor}>
                      {STRINGS.DRAWER.CVSS_SCORE}
                    </Text>
                    <Box
                      className={`${styles.value} ${styles.valueHighlight}`}
                      bg={isHighestCvss ? diffBg : 'transparent'}
                      display="inline-block"
                      px={isHighestCvss ? 2 : 0}
                      borderRadius="sm"
                    >
                      {vuln.cvss.toFixed(1)}
                      {isHighestCvss && ` ${STRINGS.COMPARISON.HIGHEST}`}
                    </Box>
                  </Box>

                  {/* Package */}
                  <Box className={styles.section}>
                    <Text className={styles.sectionLabel} color={labelColor}>
                      {STRINGS.DRAWER.PACKAGE}
                    </Text>
                    <Text
                      className={styles.packageValue}
                      fontFamily="mono"
                      fontSize="xs"
                      bg={!allPackagesSame ? diffBg : 'transparent'}
                      px={!allPackagesSame ? 2 : 0}
                      py={1}
                      borderRadius="sm"
                    >
                      {vuln.packageName}@{vuln.packageVersion}
                    </Text>
                  </Box>

                  {/* Analysis Status */}
                  <Box className={styles.section}>
                    <Text className={styles.sectionLabel} color={labelColor}>
                      {STRINGS.DRAWER.ANALYSIS_STATUS}
                    </Text>
                    <Badge
                      colorScheme={getStatusColor(vuln.kaiStatus)}
                      variant="outline"
                    >
                      {formatStatus(vuln.kaiStatus)}
                    </Badge>
                  </Box>

                  {/* Risk Factors */}
                  <Box className={styles.section}>
                    <Text className={styles.sectionLabel} color={labelColor}>
                      {STRINGS.DRAWER.RISK_FACTORS} ({vuln.riskFactors.length})
                    </Text>
                    {vuln.riskFactors.length > 0 ? (
                      <Box className={styles.riskFactorsList}>
                        {vuln.riskFactors.map((rf) => {
                          const isUnique = uniqueRiskFactors.includes(rf)
                          return (
                            <Box
                              key={rf}
                              className={`${styles.riskFactorItem} ${isUnique ? styles.riskFactorUnique : ''}`}
                              bg={isUnique ? uniqueBg : riskFactorBg}
                              borderColor={isUnique ? uniqueBorder : 'transparent'}
                            >
                              {rf.replace(/:/g, ': ')}
                              {isUnique && ' ★'}
                            </Box>
                          )
                        })}
                      </Box>
                    ) : (
                      <Text color={labelColor} fontSize="sm">
                        {STRINGS.COMPARISON.NONE}
                      </Text>
                    )}
                  </Box>

                  {/* Description */}
                  <Box className={styles.section}>
                    <Text className={styles.sectionLabel} color={labelColor}>
                      {STRINGS.DRAWER.DESCRIPTION}
                    </Text>
                    <Text className={styles.description}>
                      {vuln.description ? unescapeText(vuln.description) : STRINGS.DRAWER.NO_DESCRIPTION}
                    </Text>
                  </Box>

                  {/* Link */}
                  <Link href={vuln.link} isExternal color="blue.500" fontSize="sm">
                    {STRINGS.DRAWER.VIEW_ON_NVD} <ExternalLinkIcon mx="2px" />
                  </Link>
                </Box>
              )
            })}
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

function getStatusColor(status: string | null): string {
  if (!status) return 'blue'
  if (status === 'ai-invalid-norisk') return 'purple'
  if (status === 'invalid - norisk') return 'gray'
  return 'blue'
}

function formatStatus(status: string | null): string {
  if (!status) return STRINGS.ANALYSIS_STATUS.PENDING_REVIEW
  if (status === 'ai-invalid-norisk') return STRINGS.ANALYSIS_STATUS.AI_NO_RISK
  if (status === 'invalid - norisk') return STRINGS.ANALYSIS_STATUS.MANUAL_NO_RISK
  return status
}
